/**
 * Subtask Service
 * Handles all subtask-related API operations
 * Follows Tim-Combo patterns - using Make_Authorized_API_Call
 */

import { Make_Authorized_API_Call, API_ENDPOINTS, isSuccessResponse } from '../api/client';

interface SubtaskData {
  title: string;
  description?: string;
  parent_task_id: string;
  assigned_to?: string | null;
  family_id?: string;
  created_by?: string;
  task_type?: string;
  status?: string;
  priority_score?: number;
  category?: string;
  difficulty?: string;
}

interface SubtaskUpdateData {
  title?: string;
  description?: string;
  status?: string;
  assigned_to?: string | null;
}

/**
 * Subtask Service API methods
 */
export const subtaskService = {
  /**
   * Get all subtasks for a parent task
   */
  async getSubtasks(parentTaskId: string) {
    const response = await Make_Authorized_API_Call(
      API_ENDPOINTS.TASKS.GET(parentTaskId) + '/subtasks',
      'GET'
    );

    if (isSuccessResponse(response)) {
      return response.data?.Records || [];
    }
    throw new Error(response.message || 'Failed to fetch subtasks');
  },

  /**
   * Create a new subtask
   */
  async createSubtask(parentTaskId: string, subtaskData: SubtaskData) {
    // Ensure task_type is set to 'subtask'
    const payload = {
      ...subtaskData,
      task_type: 'subtask',
      parent_task_id: parentTaskId
    };

    const response = await Make_Authorized_API_Call(
      API_ENDPOINTS.TASKS.CREATE,
      'POST',
      payload
    );

    if (isSuccessResponse(response)) {
      return response.data?.Records?.[0] || response.data;
    }
    throw new Error(response.message || 'Failed to create subtask');
  },

  /**
   * Update a subtask
   */
  async updateSubtask(subtaskId: string, updates: SubtaskUpdateData) {
    const response = await Make_Authorized_API_Call(
      API_ENDPOINTS.TASKS.UPDATE(subtaskId),
      'PUT',
      updates
    );

    if (isSuccessResponse(response)) {
      return response.data?.Records?.[0] || response.data;
    }
    throw new Error(response.message || 'Failed to update subtask');
  },

  /**
   * Delete a subtask
   */
  async deleteSubtask(subtaskId: string) {
    const response = await Make_Authorized_API_Call(
      API_ENDPOINTS.TASKS.DELETE(subtaskId),
      'DELETE'
    );

    if (isSuccessResponse(response)) {
      return true;
    }
    throw new Error(response.message || 'Failed to delete subtask');
  },

  /**
   * Update subtask assignment
   */
  async updateSubtaskAssignment(subtaskId: string, userId: string | null) {
    const response = await Make_Authorized_API_Call(
      API_ENDPOINTS.TASKS.ASSIGN(subtaskId),
      'POST',
      {
        assignee_id: userId,
        notes: ''
      }
    );

    if (isSuccessResponse(response)) {
      return response.data?.Records?.[0] || response.data;
    }
    throw new Error(response.message || 'Failed to update subtask assignment');
  },

  /**
   * Complete a subtask
   */
  async completeSubtask(subtaskId: string, completionData = {}) {
    const response = await Make_Authorized_API_Call(
      API_ENDPOINTS.TASKS.COMPLETE(subtaskId),
      'POST',
      completionData
    );

    if (isSuccessResponse(response)) {
      return response.data?.Records?.[0] || response.data;
    }
    throw new Error(response.message || 'Failed to complete subtask');
  },

  /**
   * Get subtask progress for a parent task
   */
  async getSubtaskProgress(parentTaskId: string) {
    const subtasks = await this.getSubtasks(parentTaskId);

    const total = subtasks.length;
    const completed = subtasks.filter((st: any) => st.status === 'completed').length;
    const inProgress = subtasks.filter((st: any) => st.status === 'in_progress').length;
    const pending = subtasks.filter((st: any) => st.status === 'pending').length;

    return {
      total,
      completed,
      inProgress,
      pending,
      completionPercentage: total > 0 ? (completed / total) * 100 : 0
    };
  }
};

export default subtaskService;
